import type {
  ResearchAcceptance,
  ResearchObservation,
  ResearchOriginalIdentity,
} from "./research-identity-contract";
import type { ResearchVerification } from "./research-verification-schema";
import { researchQuotationMatches } from "./research-quotation";

const identityGaps = new Set([
  "CONTACT_IDENTIFIER_NOT_CORROBORATED",
  "BUSINESS_IDENTIFIER_NOT_CORROBORATED",
  "SELLER_BUSINESS_RELATIONSHIP_NOT_ESTABLISHED",
  "OWNERSHIP_NOT_ESTABLISHED",
]);

/** A missing identity bridge can be answered by a person; missing facts cannot. */
export function researchIdentityReviewQuestion(input: {
  subject: "person" | "business" | "link";
  acceptance: ResearchAcceptance;
  original: ResearchOriginalIdentity;
  verification?: ResearchVerification | null;
  observations: ResearchObservation[];
  value?: unknown;
}): string | null {
  const check = input.verification;
  const reason = input.acceptance.reason.replace(/^DIAGNOSTIC:/, "");
  if (
    input.subject === "business" ||
    input.acceptance.decision !== "candidate" ||
    !identityGaps.has(reason) ||
    !check?.factSupported ||
    (check.confidence ?? 0) < 0.7 ||
    check.personMatch === "different" ||
    check.businessMatch === "different" ||
    check.dealBusinessMatch === "different" ||
    check.nameValidity !== "valid"
  )
    return null;
  const quotes = check.quotes;
  if (
    !quotes.some((quote) => quote.purpose === "fact") ||
    !quotes.every((quote) =>
      input.observations.some(
        (page) =>
          page.id === quote.observationId &&
          researchQuotationMatches(page.text, quote.text),
      ),
    )
  )
    return null;
  const name = input.original.name?.trim();
  const company = input.original.businessName?.trim();
  if (!name || !company) return null;
  const words = (value: string) =>
    value
      .normalize("NFKC")
      .toLowerCase()
      .match(/[\p{L}\p{N}]+/gu) ?? [];
  const nameWords = words(name);
  if (
    input.subject === "link" &&
    (typeof input.value !== "string" ||
      words(input.value).join(" ") !== words(company).join(" "))
  )
    return null;
  // Registry records commonly use LAST, FIRST. Require the entire intake name.
  const hasPerson =
    nameWords.length >= 2 &&
    quotes.some((quote) => {
      const quoted = new Set(words(quote.text));
      return nameWords.every((word) => quoted.has(word));
    });
  const hasCompany = quotes.some((quote) =>
    words(quote.text).join(" ").includes(words(company).join(" ")),
  );
  if (!hasPerson || !hasCompany) return null;
  if (input.subject === "link") {
    return `Is ${company} the business ${name} is selling?`;
  }
  return `Is the ${name} connected to ${company} in these sources your contact?`;
}
